import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Key, Shield, Plus, Trash2, Eye, EyeOff, Loader2,
  AlertTriangle, RefreshCw, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { API } from '../../lib/api';

const PROVIDER_BADGE = {
  aws:   { label: 'AWS',   color: 'bg-orange-500/15 text-orange-300 border-orange-500/40' },
  azure: { label: 'Azure', color: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  gcp:   { label: 'GCP',   color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
};

const PROVIDER_TEMPLATE = {
  aws: `{
  "access_key_id": "AKIA...",
  "secret_access_key": "wJalrXUt...",
  "region": "us-east-1"
}`,
  azure: `{
  "tenant_id": "00000000-0000-0000-0000-000000000000",
  "client_id": "00000000-0000-0000-0000-000000000000",
  "client_secret": "your-client-secret",
  "subscription_id": "00000000-0000-0000-0000-000000000000"
}`,
  gcp: `{
  "service_account_json": {
    "type": "service_account",
    "project_id": "your-project",
    "private_key_id": "...",
    "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",
    "client_email": "svc@your-project.iam.gserviceaccount.com",
    "client_id": "..."
  }
}`,
};

export default function SandboxVault() {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);   // 503 → vault locked
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState({});  // { credId: secretDict }
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/orchestration/sandbox-creds`, { withCredentials: true },
      );
      setCreds(data.creds || []);
      setError(null);
      setLocked(false);
    } catch (e) {
      if (e?.response?.status === 503) setLocked(true);
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => {
    try {
      await axios.post(
        `${API}/orchestration/sandbox-creds`, payload,
        { withCredentials: true },
      );
      setShowCreate(false);
      await load();
    } catch (e) {
      alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(
      'Delete this credential? Any Runner Pool jobs still using it will fail on next execution.',
    )) return;
    setBusyId(id);
    try {
      await axios.delete(
        `${API}/orchestration/sandbox-creds/${id}`, { withCredentials: true },
      );
      setRevealed((r) => {
        const c = { ...r }; delete c[id]; return c;
      });
      await load();
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleReveal = async (id) => {
    if (revealed[id]) {
      setRevealed((r) => { const c = { ...r }; delete c[id]; return c; });
      return;
    }
    setBusyId(id);
    try {
      const { data } = await axios.get(
        `${API}/orchestration/sandbox-creds/${id}/reveal`,
        { withCredentials: true },
      );
      setRevealed((r) => ({ ...r, [id]: data.secret }));
    } catch (e) {
      alert(`Reveal failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen p-10 bg-slate-950 text-slate-100 flex items-center gap-3">
      <Loader2 className="w-6 h-6 animate-spin" /> Loading Sandbox Vault…
    </div>
  );

  return (
    <div className="min-h-screen p-6 md:p-10 bg-slate-950 text-slate-100" data-testid="sandbox-vault-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-purple-500/15 border border-purple-500/40">
              <Key className="w-7 h-7 text-purple-300" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="sandbox-vault-heading">
                Sandbox Vault
              </h1>
              <p className="text-slate-400 mt-1 max-w-2xl">
                Encrypted <b>sandbox-only</b> cloud credentials for Tier 2 <code>apply</code>
                runs. Never store production keys here — use Connected mode for prod.
                Secrets are Fernet-encrypted with <code>CG_VAULT_KEY</code> before hitting Mongo.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="vault-refresh-btn"
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              disabled={locked}
              onClick={() => setShowCreate(true)}
              data-testid="new-cred-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> New credential
            </button>
          </div>
        </div>

        {locked && (
          <div className="mb-6 p-5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-100 flex items-start gap-3"
               data-testid="vault-locked-banner">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold mb-1">Vault is locked — <code>CG_VAULT_KEY</code> is not set</div>
              <div className="text-sm opacity-90 mb-3">
                Generate a key and set it as an environment variable on the CloudGenie backend,
                then restart. Guide: <code>docs/guides/SANDBOX_VAULT_GUIDE.md</code>.
              </div>
              <div className="text-xs font-mono bg-slate-950/60 border border-amber-500/20 rounded p-3">
                python -c "from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())"
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm"
               data-testid="vault-error">
            {error}
          </div>
        )}

        {!locked && creds.length === 0 && (
          <div className="p-8 rounded-xl border border-dashed border-slate-700 text-center"
               data-testid="vault-empty">
            <Shield className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <div className="text-lg font-medium mb-1">No sandbox credentials yet</div>
            <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
              Add your sandbox AWS/Azure/GCP credentials to enable Tier 2 <code>apply</code>
              runs from the Runner Pool. Never paste production credentials here.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="empty-new-cred-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-medium"
            >
              <Plus className="w-4 h-4" /> Add your first credential
            </button>
          </div>
        )}

        {creds.length > 0 && (
          <div className="space-y-2" data-testid="vault-cred-list">
            {creds.map((c) => {
              const badge = PROVIDER_BADGE[c.provider] || PROVIDER_BADGE.aws;
              const secret = revealed[c.id];
              return (
                <div key={c.id}
                     className="p-4 rounded-lg border border-slate-800 bg-slate-900/40"
                     data-testid={`cred-row-${c.id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2 py-1 rounded border ${badge.color}`}>
                      {badge.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        <code>{c.id.slice(0, 20)}…</code> · used {c.use_count} times
                        {c.last_used_at && <> · last {(c.last_used_at || '').slice(0, 19).replace('T', ' ')}Z</>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleReveal(c.id)}
                      disabled={busyId === c.id}
                      data-testid={`reveal-cred-${c.id}`}
                      className="p-2 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-800 disabled:opacity-50"
                      title={secret ? 'Hide secret' : 'Reveal secret'}
                    >
                      {busyId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                        secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      disabled={busyId === c.id}
                      data-testid={`delete-cred-${c.id}`}
                      className="p-2 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                      title="Delete credential"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {secret && (
                    <pre className="mt-3 p-3 bg-slate-950 border border-slate-800 rounded text-xs font-mono overflow-x-auto text-slate-200"
                         data-testid={`cred-secret-${c.id}`}>
{JSON.stringify(secret, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 p-4 rounded-lg border border-slate-800 bg-slate-900/30 text-xs text-slate-400 flex items-start gap-2">
          <ExternalLink className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Docs:</b> <code>docs/guides/SANDBOX_VAULT_GUIDE.md</code> — plain-English setup
            for the Fernet key, per-provider credential format, and the end-to-end flow of
            how a Tier 2 job injects these secrets into <code>terraform apply</code>.
          </div>
        </div>

        {showCreate && (
          <CreateCredModal
            onClose={() => setShowCreate(false)}
            onSubmit={handleCreate}
          />
        )}
      </div>
    </div>
  );
}

function CreateCredModal({ onClose, onSubmit }) {
  const [provider, setProvider] = useState('aws');
  const [name, setName] = useState('');
  const [secretJson, setSecretJson] = useState(PROVIDER_TEMPLATE.aws);
  const [saving, setSaving] = useState(false);
  const [parseError, setParseError] = useState(null);

  const changeProvider = (p) => {
    setProvider(p);
    setSecretJson(PROVIDER_TEMPLATE[p] || '{}');
    setParseError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert('Name is required');
    let secret;
    try {
      secret = JSON.parse(secretJson);
    } catch (err) {
      setParseError(`JSON parse error: ${err.message}`);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ provider, name: name.trim(), secret });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="create-cred-modal">
      <form onSubmit={submit}
             className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">New sandbox credential</h3>
            <p className="text-xs text-slate-500 mt-1">
              Encrypted with Fernet + <code>CG_VAULT_KEY</code> before hitting Mongo. Only
              your org can decrypt it.
            </p>
          </div>
          <button type="button" onClick={onClose}
                   className="text-slate-500 hover:text-slate-200 text-2xl leading-none">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Provider</label>
            <div className="flex gap-2" data-testid="provider-picker">
              {['aws', 'azure', 'gcp'].map((p) => {
                const badge = PROVIDER_BADGE[p];
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => changeProvider(p)}
                    data-testid={`provider-${p}`}
                    className={`px-4 py-2 rounded border text-sm capitalize ${
                      provider === p
                        ? badge.color
                        : 'border-slate-700 text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {badge.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="cred-name-input"
              className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-800"
              placeholder="my-aws-sandbox"
            />
            <p className="text-xs text-slate-500 mt-1">
              Human-readable label; not sent to the cloud provider.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Secret (JSON)</label>
            <textarea
              value={secretJson}
              onChange={(e) => { setSecretJson(e.target.value); setParseError(null); }}
              data-testid="cred-secret-input"
              rows={12}
              spellCheck={false}
              className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-800 font-mono text-xs text-slate-200"
            />
            {parseError && (
              <div className="mt-2 text-xs text-rose-300" data-testid="parse-error">{parseError}</div>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Shape is provider-specific — the placeholder shows the fields expected.
              Everything you paste is encrypted before it reaches the database.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
          <button type="button" onClick={onClose}
                   className="px-4 py-2 rounded text-slate-300 hover:bg-slate-800">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="submit-cred-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded bg-purple-500 hover:bg-purple-400 text-white font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save credential'}
          </button>
        </div>
      </form>
    </div>
  );
}
